import { logInfo } from '@core/logging'
import type { Socket } from '@core/phoenix/core/socket'
import type {
  PhoenixPushEventMap,
  PhoenixServerEventMap,
  StringKeyof,
} from '@core/phoenix/core/types'

import { Push } from './push'
import { Timer } from './timer'

type ChannelState = 'closed' | 'errored' | 'joined' | 'joining' | 'leaving'

interface Binding<
  ServerEventMap extends PhoenixServerEventMap = PhoenixServerEventMap,
  K extends StringKeyof<ServerEventMap> = StringKeyof<ServerEventMap>,
> {
  event: K
  ref: number
  callback: (payload: ServerEventMap[K], ref?: string) => void
}

/** Channel class. */
export class Channel<
  PushEventMap extends PhoenixPushEventMap = PhoenixPushEventMap,
  ServerEventMap extends PhoenixServerEventMap = PhoenixServerEventMap,
> {
  /** The socket that the channel belongs to. */
  private _socket: Socket

  /** The channel topic. */
  private _topic: string

  /** The channel state. */
  private state: ChannelState

  /** The callback bindings to the server events. */
  private bindings: Binding<ServerEventMap>[] = []

  /** Current binding ref count. */
  private bindingRef = 0

  /** Whether the chance has joined once. */
  private joinedOnce = false

  /** The join push timeout. */
  private timeout: number

  /** The join push. */
  private joinPush: Push<PushEventMap, 'phx_join'>

  /** The buffer for pushes sent before joining the channel. */
  private pushBuffer: Push[] = []

  /** The timer for rejoining. */
  private rejoinTimer: Timer

  /** Socket state change callback refs. */
  private _stateChangeRefs: string[] = []

  /**
   * @constructor
   * @param topic The channel topic.
   * @param params The payload for the join message.
   * @param socket The socket that creates the channel.
   */
  constructor(
    topic: string,
    private readonly params: PushEventMap['phx_join']['request_payload'],
    socket: Socket,
  ) {
    this.state = 'closed'
    this._topic = topic
    this.params = params
    this._socket = socket
    this.timeout = this.socket.pushTimeout
    this.joinPush = new Push<PushEventMap, 'phx_join'>(
      this,
      'phx_join',
      this.params,
      this.timeout,
    )

    this.rejoinTimer = new Timer(() => {
      if (this.socket.isConnected()) {
        this.rejoin()
      }
    }, this.socket.rejoinAfterMs)
    this._stateChangeRefs.push(
      this.socket.onError(() => this.rejoinTimer.reset()),
    )
    this._stateChangeRefs.push(
      this.socket.onOpen(() => {
        this.rejoinTimer.reset()
        if (this.isErrored()) {
          this.rejoin()
        }
      }),
    )
    this.joinPush.receive('ok', () => {
      this.state = 'joined'
      this.rejoinTimer.reset()
      this.pushBuffer.forEach((pushEvent) => pushEvent.send())
      this.pushBuffer = []
    })
    this.joinPush.receive('error', () => {
      this.state = 'errored'
      if (this.socket.isConnected()) {
        this.rejoinTimer.scheduleTimeout()
      }
    })
    this.onClose(() => {
      this.rejoinTimer.reset()
      logInfo('channel', `close ${this.topic} ${this.joinRef()}`)
      this.state = 'closed'
      this.socket.remove(this)
    })
    this.onError((reason) => {
      logInfo('channel', `error ${this.topic}`, reason)
      if (this.isJoining()) {
        this.joinPush.reset()
      }
      this.state = 'errored'
      if (this.socket.isConnected()) {
        this.rejoinTimer.scheduleTimeout()
      }
    })
    this.joinPush.receive('timeout', () => {
      logInfo(
        'channel',
        `timeout ${this.topic} (${this.joinRef()})`,
        this.joinPush.timeout,
      )
      const leavePush = new Push(this, 'phx_leave', {}, this.timeout)
      leavePush.send()
      this.state = 'errored'
      this.joinPush.reset()
      if (this.socket.isConnected()) {
        this.rejoinTimer.scheduleTimeout()
      }
    })
    this.on('phx_reply', (payload, ref) => {
      this.trigger(this.replyEventName(ref), payload)
    })
  }

  /** @returns Socket state change refs. */
  get stateChangeRefs() {
    return this._stateChangeRefs
  }

  /** @returns The socket that created this channel. */
  get socket() {
    return this._socket
  }

  /** @return The channel topic. */
  get topic() {
    return this._topic
  }

  /**
   * Joins the channel
   *
   * @param timeout The timeout for the join push.
   * @returns The push object.
   */
  join(timeout = this.timeout) {
    if (this.joinedOnce) {
      throw new Error(
        "tried to join multiple times. 'join' can only be called a single time per channel instance",
      )
    } else {
      this.timeout = timeout
      this.joinedOnce = true
      this.rejoin()
      return this.joinPush
    }
  }

  /** Registers callback when the channel closes. */
  onClose(callback: () => void) {
    this.on('phx_close', callback)
  }

  /** Registers callback when a channel error occurs. */
  onError(callback: (reason: ServerEventMap['phx_error']) => void) {
    return this.on('phx_error', (reason) => callback(reason))
  }

  /**
   * Subscribes on channel events
   *
   * @example
   * const ref1 = channel.on("event", do_stuff)
   * const ref2 = channel.on("event", do_other_stuff)
   * channel.off("event", ref1)
   * // Since unsubscription, do_stuff won't fire,
   * // while do_other_stuff will keep firing on the "event"
   *
   * @param event The event name.
   * @param callback The function to trigger on receiving the event.
   * @returns The ref used to unsubscribe the event listener.
   */
  on<E extends StringKeyof<ServerEventMap>>(
    event: E,
    callback: (payload: ServerEventMap[E], ref?: string) => void,
  ) {
    const ref = this.bindingRef++
    this.bindings.push({ event, ref, callback })
    return ref
  }

  /**
   * Unsubscribes from channel events
   *
   * Use the ref returned from a channel.on() to unsubscribe one
   * handler, or pass nothing for the ref to unsubscribe all
   * handlers for the given event.
   *
   * @example
   * // Unsubscribe the do_stuff handler
   * const ref1 = channel.on("event", do_stuff)
   * channel.off("event", ref1)
   *
   * // Unsubscribe all handlers from event
   * channel.off("event")
   */
  off<E extends StringKeyof<ServerEventMap>>(event: E, ref?: number) {
    this.bindings = this.bindings.filter((bind) => {
      return !(
        bind.event === event &&
        (typeof ref === 'undefined' || ref === bind.ref)
      )
    })
  }

  /**
   * @returns Whether it can push events to the channel.
   */
  private canPush() {
    return this.socket.isConnected() && this.isJoined()
  }

  /**
   * Sends a message `event` to phoenix with the `payload`.
   * Phoenix receives this in the `handle_in(event, payload, socket)`
   * function. if phoenix replies or it times out (default 10000ms),
   * then optionally the reply can be received.
   *
   * @example
   * channel.push("event")
   *   .receive("ok", payload => console.log("phoenix replied:", payload))
   *   .receive("error", err => console.log("phoenix errored", err))
   *   .receive("timeout", () => console.log("timed out pushing"))
   *
   * @param event The event name.
   * @param payload The payload to send.
   * @param timeout The timeout in milliseconds.
   * @returns The push object.
   */
  push<E extends StringKeyof<PushEventMap>>(
    event: E,
    payload: PushEventMap[E]['request_payload'],
    timeout = this.timeout,
  ) {
    if (!this.joinedOnce) {
      throw new Error(
        `tried to push '${event}' to '${this.topic}' before joining. Use channel.join() before pushing events`,
      )
    }
    const pushEvent = new Push(this, event, payload, timeout)
    if (this.canPush()) {
      pushEvent.send()
    } else {
      pushEvent.startTimeout()
      this.pushBuffer.push(pushEvent)
    }

    return pushEvent
  }

  /**
   * Leaves the channel
   *
   * Unsubscribes from server events, and
   * instructs channel to terminate on server
   *
   * Triggers onClose() hooks
   *
   * To receive leave acknowledgements, use the `receive`
   * hook to bind to the server ack, ie:
   *
   * @example
   * channel.leave().receive("ok", () => alert("left!") )
   *
   * @param {integer} timeout
   * @returns {Push}
   */
  leave(timeout = this.timeout) {
    this.rejoinTimer.reset()
    this.joinPush.cancelTimeout()

    this.state = 'leaving'
    const onClose = () => {
      logInfo('channel', `leave ${this.topic}`)
    }
    const leavePush = new Push(this, 'phx_leave', {}, timeout)
    leavePush.receive('ok', () => onClose()).receive('timeout', () => onClose())
    leavePush.send()
    if (!this.canPush()) {
      leavePush.trigger('ok', {})
    }

    return leavePush
  }

  /**
   * @returns Whether the event is a member of the channel.
   */
  isMember(topic: string, joinRef?: string) {
    if (this.topic !== topic) {
      return false
    }

    if (joinRef && joinRef !== this.joinRef()) {
      return false
    } else {
      return true
    }
  }

  /**
   * @returns The join event ref.
   */
  joinRef() {
    return this.joinPush.ref
  }

  /** Rejoins the channel. */
  private rejoin() {
    if (this.isLeaving()) {
      return
    }
    this.socket.leaveOpenTopic(this.topic)
    this.state = 'joining'
    this.joinPush.resend()
  }

  /**
   * Triggers the callbacks bound to the event.
   */
  trigger<E extends StringKeyof<ServerEventMap>>(
    event: E,
    payload: ServerEventMap[E],
    ref?: string,
  ) {
    const eventBindings = this.bindings.filter((bind) => bind.event === event)

    for (let i = 0; i < eventBindings.length; i++) {
      const bind = eventBindings[i]
      bind.callback(payload, ref)
    }
  }

  /**
   * @returns The internal reply event name.
   */
  replyEventName(ref) {
    return `chan_reply_${ref}`
  }

  /**
   * @returns Whether the channel is closed.
   */
  isClosed() {
    return this.state === 'closed'
  }

  /**
   * @returns Whether the channel is errored.
   */
  isErrored() {
    return this.state === 'errored'
  }

  /**
   * @returns Whether the channel is joined.
   */
  isJoined() {
    return this.state === 'joined'
  }

  /**
   * @returns Whether the channel is joining.
   */
  isJoining() {
    return this.state === 'joining'
  }

  /**
   * @returns Whether the channel is leaving.
   */
  isLeaving() {
    return this.state === 'leaving'
  }
}
